import { useMemo } from "react";
import { useSelector } from "react-redux";
import { Trash2 } from "lucide-react";

import SelectDropdownV2 from "../../../components/ui/selectdropdown/v2";
import { Textarea } from "../../../components/ui/textbox/TextBox";
import { OptionNameValuePair } from "../../../lib/interfaces/types";
import {
  FRAUD_ELEMENT_CATEGORIES_LABELS,
  FraudElementCategories,
  FraudElementInput,
} from "../../../api/fraudElements";
import { PartyInvolved } from "../../../api/PartiesInvolved";
import { selectPartiesWithSCNAction } from "../../recommendations/logic/ActionsAgainstPerpetratorsSlice";

export const getSCNPartyOptions = (
  parties: PartyInvolved[],
): OptionNameValuePair<string>[] =>
  parties.map((party) => ({
    name: party.name,
    value: party.party_id,
  }));

interface FraudElementFormProps {
  index: number;
  fraudElement: FraudElementInput;
  onChange: <K extends keyof FraudElementInput>(
    field: K,
    value: FraudElementInput[K]
  ) => void;
  onDelete: () => void;
}

export const CATEGORY_OPTIONS: OptionNameValuePair<FraudElementCategories>[] =
  Object.values(FraudElementCategories).map((category) => ({
    value: category,
    name: FRAUD_ELEMENT_CATEGORIES_LABELS[category],
  }));

export default function FraudElementForm({
  index,
  fraudElement,
  onChange,
  onDelete,
}: FraudElementFormProps) {
  const scnParties = useSelector(selectPartiesWithSCNAction);

  const partyOptions = useMemo(
    () => getSCNPartyOptions(scnParties),
    [scnParties]
  );


  const selectedParty = partyOptions.find(
    (option) => option.value === fraudElement.party_id
  );

  const selectedCategory = CATEGORY_OPTIONS.find(
    (option) => option.value === fraudElement.fraud_element_category
  );

  return (
    <div className="flex flex-col gap-4 rounded-[8px] bg-[#F9FAFB] p-3 font-[Inter]">
      <div className="flex items-center justify-between">
        <span className="text-[12px] font-medium leading-none text-[#4B5563]">
          Fraud Element {index + 1}
        </span>

        {index >= 0 && (
          <button
            type="button"
            onClick={onDelete}
            className="flex items-center justify-center text-[#0A0A0A] opacity-50"
          >
            <Trash2 size={16} />
          </button>
        )}
      </div>

      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="flex flex-col gap-2 min-w-0">
            <label className="text-[14px] font-medium leading-none text-[#0A0A0A]">
              Party Involved<span className="text-[#DC2626]">*</span>
            </label>
            <SelectDropdownV2
              name="party involved"
              enableSearch
              options={partyOptions}
              selectedOption={selectedParty}
              onSelectionChange={(option) => onChange("party_id", option.value)}
              containerClassName="w-full min-w-0"
              labelClassName="h-[36px] py-2 px-3 border border-[#E5E5E5] bg-white rounded-[8px] shadow-[0_1px_2px_rgba(0,0,0,0.05)] text-sm text-[#0A0A0A] focus:outline-none min-w-0 flex items-center justify-between overflow-hidden [&_div]:!w-full [&_div]:!min-w-0 [&_div]:!flex-1 [&_span]:!block [&_span]:!truncate [&_span]:!w-full"
              titleClassName="truncate block max-w-full overflow-hidden text-ellipsis whitespace-nowrap"
              dropdownClassName="!w-[260px] !max-w-[260px] overflow-x-hidden"
              optionClassName="!whitespace-nowrap !truncate !block !w-full overflow-hidden text-ellipsis [&_span]:!inline-block [&_span]:!truncate [&_span]:!max-w-[85%] [&_span]:!overflow-hidden [&_span]:!text-ellipsis"
            />
          </div>

          <div className="flex flex-col gap-2 min-w-0">
            <label className="text-sm font-medium leading-none text-[#0A0A0A]">
              Category<span className="text-[#DC2626]">*</span>
            </label>
            <SelectDropdownV2
              name="category"
              enableSearch
              options={CATEGORY_OPTIONS}
              selectedOption={selectedCategory}
              showTooltip={true}
              showOptionTooltip={true}
              onSelectionChange={(option) =>
                onChange("fraud_element_category", option.value)
              }
              containerClassName="w-full min-w-0"
              labelClassName="h-[36px] py-2 px-3 border border-[#E5E5E5] bg-white rounded-[8px] shadow-[0_1px_2px_rgba(0,0,0,0.05)] text-sm text-[#0A0A0A] focus:outline-none min-w-0 flex items-center justify-between overflow-hidden [&_div]:!w-full [&_div]:!min-w-0 [&_div]:!flex-1 [&_span]:!block [&_span]:!truncate [&_span]:!w-full"
              titleClassName="truncate block max-w-full overflow-hidden text-ellipsis whitespace-nowrap"
              dropdownClassName="!w-[260px] !max-w-[260px] overflow-x-hidden"
              optionClassName="!whitespace-nowrap !truncate !block !w-full overflow-hidden text-ellipsis [&_span]:!inline-block [&_span]:!truncate [&_span]:!max-w-[85%] [&_span]:!overflow-hidden [&_span]:!text-ellipsis"
            />
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium leading-none text-[#0A0A0A]">
            Details (Optional)
          </span>
          <Textarea
            value={fraudElement.details ?? ""}
            placeholder="Enter fraud element details..."
            rows={5}
            onChange={(e) => onChange("details", e.target.value)}
            style={{ resize: "none" }}
            className="h-[117px] py-2 px-3 border border-[#E5E5E5] bg-white rounded-[8px] shadow-[0_1px_2px_rgba(0,0,0,0.05)] text-sm text-[#0A0A0A] placeholder:text-[#737373] placeholder:leading-5 focus:outline-none focus:ring-0 focus:ring-transparent focus:border-[#E5E5E5]"
          />
        </div>
      </div>
    </div>
  );
}